import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../hd-content-language.service';
import { HdDrawChartGameService } from '../hd-draw-chart-game.service';
import { HdGameFeedbackAudioService } from '../../hd-game-feedback-audio.service';

/**
 * Spel-paneel voor **"Teken je chart"**. Toont het hulpscherm met de
 * instructie om de geselecteerde chart na te tekenen, een voortgangsbalk,
 * een Submit- en een Hint-knop (beide tonen een willekeurige hint zolang de
 * chart nog niet klopt), de huidige hint, de score en — bij winst — een
 * eindscherm met sterren/trofee. Sluit visueel volledig aan bij de andere
 * spel-panelen (Poortenjacht / Kanalenjacht).
 */
@Component({
  selector: 'hd-draw-chart-game-panel',
  imports: [SharedModule],
  templateUrl: './hd-draw-chart-game-panel.component.html',
  styleUrl: './hd-draw-chart-game-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdDrawChartGamePanelComponent {
  readonly restartRequested = output<void>();

  protected readonly game = inject(HdDrawChartGameService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly feedbackAudio = inject(HdGameFeedbackAudioService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Effectieve content-taal (override uit het controls-paneel of de app-taal). */
  protected readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly audioEnabled = this.feedbackAudio.enabled;

  /** Voortgangsbalk standaard verborgen; toggle-knop in het paneel toont/verbergt hem. */
  protected readonly progressVisible = signal(false);

  protected readonly isFinished = this.game.isFinished;
  protected readonly isPerfectGame = this.game.isPerfectGame;
  protected readonly targetName = this.game.targetName;
  protected readonly currentHint = this.game.currentHint;
  protected readonly hintsUsed = this.game.hintsUsed;
  protected readonly matchPercentage = this.game.matchPercentage;
  protected readonly matchedGates = this.game.matchedGates;
  protected readonly targetGateCount = this.game.targetGateCount;
  protected readonly submitErrors = this.game.submitErrors;
  protected readonly checkAllCorrect = this.game.checkAllCorrect;
  protected readonly maxPossibleScore = this.game.maxPossibleScore;

  protected readonly scoreLabel = computed(() => `${this.game.score()}`);

  /** Sterren-rating (1–5) afgeleid van de eindscore, voor visuele consistentie. */
  protected readonly scoreLevel = computed<number>(() => {
    const score = this.game.score();
    if (score >= 100) return 5;
    if (score >= 90) return 4;
    if (score >= 75) return 3;
    if (score >= 50) return 2;
    return 1;
  });

  protected readonly starIndices = computed(() => indicesArray(this.scoreLevel()));
  protected readonly emptyStarIndices = computed(() => indicesArray(5 - this.scoreLevel()));

  /**
   * Vertaalt een sleutel in de **effectieve content-taal** (de override uit het
   * controls-paneel, of de app-taal). Leest `lang()` zodat het paneel reactief
   * mee verandert wanneer de speler de taal in het controls-paneel wisselt.
   */
  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params, this.lang());
  }

  protected toggleAudio(): void {
    this.feedbackAudio.toggleEnabled();
  }

  protected toggleProgress(): void {
    this.progressVisible.update(visible => !visible);
  }

  protected submit(): void {
    if (this.game.submit()) {
      this.feedbackAudio.playCorrect();
    }
  }

  protected requestHint(): void {
    this.game.requestHint();
  }

  protected async requestRestart(): Promise<void> {
    if (!this.game.isActive() || this.isFinished()) {
      this.restartRequested.emit();
      return;
    }

    const confirmed = await this.confirmer.confirm(
      this.t('humanDesign.games.drawChart.restartConfirm'),
      { color: 'danger' },
    );

    if (confirmed) {
      this.restartRequested.emit();
    }
  }
}

function indicesArray(count: number): readonly number[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => i);
}
